import { useCallback } from 'react';
import { useBookkeepingConfiguration } from '~/services/bookkeeping/bookkeeping-configuration';
import { useBookkeepingSettings, useUpsertBookkeepingSettingsAccounts } from '~/services/company-settings';
import { BookkeepingAccountsSettings } from '~/types/company-settings';
import { BookkeepingAdjustmentType } from '~/types/general-bookkeeping';

// Adjustment type → company-settings account field. Types absent here (e.g. AMMORTAMENTO) resolve to null.
const SETTINGS_FIELD_BY_TYPE: Partial<Record<BookkeepingAdjustmentType, keyof BookkeepingAccountsSettings>> = {
  [BookkeepingAdjustmentType.RISCONTO_ATTIVO]: 'prepaidExpense',
  [BookkeepingAdjustmentType.RISCONTO_PASSIVO]: 'deferredRevenue',
  [BookkeepingAdjustmentType.RATEO_ATTIVO]: 'accruedIncome',
  [BookkeepingAdjustmentType.RATEO_PASSIVO]: 'accruedExpense',
  [BookkeepingAdjustmentType.FATTURA_DA_EMETTERE]: 'invoicesToIssue',
  [BookkeepingAdjustmentType.FATTURA_DA_RICEVERE]: 'invoicesToReceive',
  [BookkeepingAdjustmentType.NOTA_CREDITO_DA_EMETTERE]: 'creditNotesToIssue',
  [BookkeepingAdjustmentType.NOTA_CREDITO_DA_RICEVERE]: 'creditNotesToReceive',
};

/** Adjustment accounts sourced from company settings, replacing the old localStorage memory. */
export function useAdjustmentAccountSettings() {
  const { accounts } = useBookkeepingConfiguration();
  const rawSettings = useBookkeepingSettings();
  const { mutate: upsertAccounts } = useUpsertBookkeepingSettingsAccounts();

  const recall = useCallback(
    (type: BookkeepingAdjustmentType): string | null => {
      const field = SETTINGS_FIELD_BY_TYPE[type];
      return field ? (accounts[field] ?? null) : null;
    },
    [accounts],
  );

  const save = useCallback(
    (type: BookkeepingAdjustmentType, accountId: string) => {
      const field = SETTINGS_FIELD_BY_TYPE[type];
      if (field && !rawSettings?.accounts?.[field]) {
        upsertAccounts({ [field]: accountId });
      }
    },
    [rawSettings, upsertAccounts],
  );

  return { recall, save };
}
